import { useCallback, useEffect, useState } from "react";
import {
  assetUsagesFromError,
  deleteAsset,
  describeAssetError,
  listAssets,
  uploadAsset,
  type AssetKind,
  type AssetUsage,
  type BroadcastAsset,
} from "../../lib/broadcastApi";

export interface AssetError {
  status: number | null;
  detail: string;
}

export interface PendingDelete {
  asset: BroadcastAsset;
  usages: AssetUsage[];
}

/** The broadcaster's image library, loaded with the control token. */
export function useAssetLibrary(token: string, paused = false) {
  const [assets, setAssets] = useState<BroadcastAsset[]>([]);
  const [error, setError] = useState<AssetError | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingDelete | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      setAssets(await listAssets(token));
    } catch (err) {
      setError(describeAssetError(err));
    }
  }, [token]);

  useEffect(() => {
    if (!paused) void reload();
  }, [reload, paused]);

  const upload = useCallback(
    async (kind: AssetKind, file: File): Promise<BroadcastAsset | null> => {
      setBusy(true);
      setError(null);
      try {
        const created = await uploadAsset(token, kind, file);
        await reload();
        return created;
      } catch (err) {
        setError(describeAssetError(err));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [token, reload],
  );

  /** Delete; a 409 with usages opens the confirm (force unless on air). */
  const remove = useCallback(
    async (asset: BroadcastAsset, force = false) => {
      setBusy(true);
      setError(null);
      try {
        await deleteAsset(token, asset.asset_id, force);
        setPending(null);
        await reload();
      } catch (err) {
        const usages = assetUsagesFromError(err);
        if (usages) setPending({ asset, usages });
        else setError(describeAssetError(err));
      } finally {
        setBusy(false);
      }
    },
    [token, reload],
  );

  return {
    assets,
    error,
    busy,
    pending,
    upload,
    remove,
    cancelDelete: () => setPending(null),
    clearError: () => setError(null),
  };
}

export type AssetLibrary = ReturnType<typeof useAssetLibrary>;
